/**
 * Was in einem Behälter ist -- Packstück oder Lagerort (ADR 0013 §2).
 *
 * Aus der lokalen Kopie, also auch offline, und mit dem, was noch in der
 * Warteschlange liegt. Feste Orte hängen wie in den Stammdaten; Packstücke
 * dort, wo sie gebucht sind (ADR 0013 §13).
 */

import type { Artikel, Lagerort, Zustand } from "@thw/contracts";
import type { Bestand } from "./bestand.js";
import { uebergeordnet, unterorte } from "./verschieben.js";

export interface Eintrag {
  artikel: Artikel;
  zustand: Zustand;
}

/** Gleiche Artikel zusammengefasst: „5× Leitung CEE 125 A“. */
export interface Gruppe {
  bezeichnung: string;
  eintraege: Eintrag[];
}

export interface Unterort {
  lagerort: Lagerort;
  /** Artikel direkt darin. */
  anzahl: number;
}

export interface BehaelterAnsicht {
  lagerort: Lagerort;
  /** Worin er gerade steckt. */
  oben: Lagerort | null;
  /** Der Artikel, der dieses Packstück ist -- zum Verschieben und für den Status. */
  eigenerArtikel: Artikel | null;
  /** Feste Plätze darin: Fächer, Regale. */
  plaetze: Unterort[];
  /** Packstücke darin -- eigene Zeilen, nicht als Artikel. */
  packstuecke: Unterort[];
  gruppen: Gruppe[];
  /** Artikel direkt hier, ohne die Packstücke. */
  anzahlArtikel: number;
}

export function behaelterAnsicht(bestand: Bestand, id: string): BehaelterAnsicht | null {
  const lagerort = bestand.lagerorte.get(id);
  if (lagerort === undefined) return null;

  const anzahlIn = new Map<string, number>();
  for (const z of bestand.zustaende.values()) {
    if (z.lagerort_id !== null) anzahlIn.set(z.lagerort_id, (anzahlIn.get(z.lagerort_id) ?? 0) + 1);
  }

  // Wer ein Packstück ist, steht als eigene Zeile da, nicht im Inhalt.
  const packstueckArtikel = new Set<string>();
  for (const l of bestand.lagerorte.values()) {
    if (l.artikel_id !== null) packstueckArtikel.add(l.artikel_id);
  }

  const plaetze: Unterort[] = [];
  const packstuecke: Unterort[] = [];
  for (const u of unterorte(bestand, id)) {
    const eintrag = { lagerort: u, anzahl: anzahlIn.get(u.id) ?? 0 };
    if (u.artikel_id === null) plaetze.push(eintrag);
    else packstuecke.push(eintrag);
  }

  const nachName = new Map<string, Eintrag[]>();
  for (const [artikel_id, zustand] of bestand.zustaende) {
    if (zustand.lagerort_id !== id || packstueckArtikel.has(artikel_id)) continue;
    const artikel = bestand.artikel.get(artikel_id);
    if (artikel === undefined) continue;
    const liste = nachName.get(artikel.bezeichnung);
    if (liste === undefined) nachName.set(artikel.bezeichnung, [{ artikel, zustand }]);
    else liste.push({ artikel, zustand });
  }
  const gruppen = [...nachName]
    .map(([bezeichnung, eintraege]) => ({
      bezeichnung,
      eintraege: eintraege.sort((a, b) =>
        a.artikel.inventarnummer.localeCompare(b.artikel.inventarnummer, "de"),
      ),
    }))
    .sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung, "de"));

  return {
    lagerort,
    oben: uebergeordnet(bestand, lagerort),
    eigenerArtikel:
      lagerort.artikel_id === null ? null : (bestand.artikel.get(lagerort.artikel_id) ?? null),
    plaetze,
    packstuecke,
    gruppen,
    anzahlArtikel: gruppen.reduce((s, g) => s + g.eintraege.length, 0),
  };
}
